import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SdvorIconComponent } from '@sdvor/shared/ui/v2';
import { YandexMapModule } from '@sdvor/yandex-maps/ui';
import { DeviceService } from '@shared/services';
import { IconTypes } from '@shared/v2';
import { WindowRef } from '@spartacus/core';
import { GeocodeResult } from '@yandex-maps/const';
import { GeocodeService, YmapsService } from '@yandex-maps/feature';
import { filter, take } from 'rxjs';
import { DeliveryAddressDialogComponentService } from '../delivery-address-dialog.component.service';

@Component({
  selector: 'sd-delivery-address-map',
  imports: [YandexMapModule, SdvorIconComponent],
  templateUrl: './delivery-address-map.component.html',
  styleUrls: ['./delivery-address-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryAddressMapComponent {
  private geocodeService = inject(GeocodeService);
  private destroyRef = inject(DestroyRef);
  private deviceService = inject(DeviceService);
  private winRef = inject(WindowRef);
  private ymapsService = inject(YmapsService);
  private deliveryAddressDialogService = inject(DeliveryAddressDialogComponentService);

  balloon: ymaps.Balloon;
  private mapInstance: ymaps.Map;

  protected readonly iconType = IconTypes;

  private readonly MAX_ZOOM_IN = 18;
  private readonly MAX_ZOOM_OUT = 2;

  protected readonly isTabletMdDown = toSignal(this.deviceService.isTabletMdDown$, { initialValue: false });

  onMapZoom(zoomType: 'IN' | 'OUT'): void {
    if (this.mapInstance) {
      const currentZoom = this.mapInstance.getZoom();
      if (currentZoom < this.MAX_ZOOM_IN && zoomType === 'IN') {
        this.mapInstance.setZoom(currentZoom + 1, { duration: 300 });
      } else if (currentZoom > this.MAX_ZOOM_OUT && zoomType === 'OUT') {
        this.mapInstance.setZoom(currentZoom - 1, { duration: 300 });
      }
    }
  }

  onMapGetGeolocation(): void {
    if (!this.mapInstance || !this.winRef.isBrowser()) {
      return;
    }

    this.ymapsService.ymaps$.pipe(take(1)).subscribe((ymaps) => {
      const y = ymaps as any;

      y.geolocation
        .get({ timeout: 10000 })
        .then((result: any) => {
          const geoObject = result.geoObjects.get(0);
          const coords: ymaps.Coordinate = geoObject.geometry.getCoordinates();

          this.geocodeService
            .getGeoObject(coords)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((geocodeResult: GeocodeResult) => {
              this.mapInstance.geoObjects.removeAll();
              this.mapInstance.setZoom(this.MAX_ZOOM_IN);
              this.mapInstance.setCenter(coords);
              this.openBalloon(coords, geocodeResult.getAddressLine());
              this.deliveryAddressDialogService.setGeoObject({
                geocodeResult,
                exactPoint: { latitude: coords[0], longitude: coords[1] },
              });
            });
        })
        .catch(() => {});
    });
  }

  onMapCreated(mapInstance: ymaps.Map): void {
    this.mapInstance = mapInstance;
    this.createBalloon(mapInstance);

    mapInstance.events.add('click', this.onMapClick);

    this.deliveryAddressDialogService.suggestedCoords$
      .pipe(filter<ymaps.Coordinate>(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe((coordinates) => {
        this.geocodeService
          .getAddressByCoords(coordinates)
          .pipe(take(1))
          .subscribe((address) => {
            this.mapInstance.geoObjects.removeAll();
            this.balloon.close();
            this.mapInstance.setZoom(14);
            this.mapInstance.setCenter(coordinates);
            this.mapInstance.geoObjects.add(
              new ymaps.Placemark(
                coordinates,
                { iconCaption: address.split(',').slice(2).join(',') },
                { id: 'userPlacemark', preset: 'islands#orangeCircleDotIcon' },
              ),
            );
          });
      });
  }

  onMapClick = (e: ymaps.Event): void => {
    const coords = e.get<ymaps.Coordinate>('coords');
    this.geocodeService
      .getGeoObject(coords)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((geocodeResult: GeocodeResult) => {
        this.mapInstance?.geoObjects.removeAll();
        this.openBalloon(coords, geocodeResult.getAddressLine());
        this.deliveryAddressDialogService.setGeoObject({
          geocodeResult,
          exactPoint: { latitude: coords[0], longitude: coords[1] },
        });
      });
  };

  createBalloon(mapInstance: ymaps.Map): void {
    this.balloon = new ymaps.Balloon(mapInstance, {
      panelMaxMapArea: 400,
      maxWidth: 200,
    });

    this.balloon.options.setParent(mapInstance.options);
  }

  openBalloon(coords: ymaps.Coordinate, address: string): void {
    this.balloon.close();
    this.balloon.open(coords, address);
  }
}
